/**
 * Sample loader: fetches and decodes the audio sprites / ambience loops listed in
 * manifest.gen.ts, lazily and by group. Opus-in-WebM is preferred; browsers that can't play it
 * (or fail to decode it) get the MP3 copy. Decoding uses an OfflineAudioContext when available,
 * so sprites can be ready before the first gesture creates the real context.
 *
 * Never throws and never rejects: a missing or broken file resolves to null and the engine falls
 * back to the synthesized recipe (or silence).
 */
import { AUDIO_MANIFEST, type ClipEntry } from './manifest.gen';

export type Fetcher = (url: string) => Promise<ArrayBuffer>;
export type Decoder = (data: ArrayBuffer) => Promise<AudioBuffer>;

export interface ClipRef {
  buffer: AudioBuffer;
  start: number;
  dur: number;
}

function defaultBase(): string {
  try {
    const b = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
    return `${b.endsWith('/') ? b : `${b}/`}assets/audio/`;
  } catch {
    return '/assets/audio/';
  }
}

function canOpus(): boolean {
  try {
    if (typeof document === 'undefined') return false;
    const a = document.createElement('audio');
    return !!a.canPlayType && a.canPlayType('audio/webm; codecs="opus"') !== '';
  } catch {
    return false;
  }
}

const defaultFetch: Fetcher = async (url) => {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return r.arrayBuffer();
};

export class SampleBank {
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly pending = new Map<string, Promise<AudioBuffer | null>>();
  private readonly failed = new Set<string>();
  private offline: BaseAudioContext | null = null;
  /** Replaceable for tests. */
  fetcher: Fetcher = defaultFetch;
  decoder: Decoder | null = null;
  base = defaultBase();
  /** File extensions to try, in order. */
  exts: readonly string[] = canOpus() ? ['webm', 'mp3'] : ['mp3'];

  constructor(private readonly manifest = AUDIO_MANIFEST) {}

  /** Provide the realtime context (used to decode when no OfflineAudioContext exists). */
  attach(ctx: BaseAudioContext): void {
    if (!this.decoder) this.decoder = (d) => ctx.decodeAudioData(d);
  }

  private decode(data: ArrayBuffer): Promise<AudioBuffer> {
    if (this.decoder) return this.decoder(data);
    const g = globalThis as { OfflineAudioContext?: typeof OfflineAudioContext; webkitOfflineAudioContext?: typeof OfflineAudioContext };
    const Off = g.OfflineAudioContext ?? g.webkitOfflineAudioContext;
    if (!Off) return Promise.reject(new Error('no decoder'));
    this.offline ??= new Off(1, 1, 48000);
    return this.offline.decodeAudioData(data);
  }

  /** Decoded buffer for a sprite / loop file, or null when not (yet) loaded. */
  get(file: string): AudioBuffer | null {
    return this.buffers.get(file) ?? null;
  }

  isFailed(file: string): boolean {
    return this.failed.has(file);
  }

  /** Load a file (base name without extension). Resolves to null on any failure. */
  load(file: string): Promise<AudioBuffer | null> {
    const have = this.buffers.get(file);
    if (have) return Promise.resolve(have);
    if (this.failed.has(file)) return Promise.resolve(null);
    let p = this.pending.get(file);
    if (p) return p;
    p = (async (): Promise<AudioBuffer | null> => {
      for (const ext of this.exts) {
        try {
          const data = await this.fetcher(`${this.base}${file}.${ext}`);
          const buf = await this.decode(data);
          this.buffers.set(file, buf);
          return buf;
        } catch {
          /* next format */
        }
      }
      this.failed.add(file);
      return null;
    })().catch(() => null);
    this.pending.set(file, p);
    void p.then(() => this.pending.delete(file));
    return p;
  }

  /** Load a sprite group ('combat' | 'ui' | 'meta'). */
  loadGroup(group: string): Promise<AudioBuffer | null> {
    const s = this.manifest.sprites[group];
    return s ? this.load(s.file) : Promise.resolve(null);
  }

  /** Load an ambience loop by key ('amb.dungeon'). */
  loadLoop(key: string): Promise<AudioBuffer | null> {
    const l = this.manifest.loops[key];
    return l ? this.load(l.file) : Promise.resolve(null);
  }

  loopBuffer(key: string): AudioBuffer | null {
    const l = this.manifest.loops[key];
    return l ? this.get(l.file) : null;
  }

  loopDuration(key: string): number {
    return this.manifest.loops[key]?.dur ?? 0;
  }

  /** Where the seamless loop starts in its file (shipped loops carry wrap-around padding). */
  loopStart(key: string): number {
    return this.manifest.loops[key]?.start ?? 0;
  }

  /** A playable clip ("hit.blade#2"), or null when its sprite isn't loaded. */
  clip(key: string): ClipRef | null {
    const c: ClipEntry | undefined = this.manifest.clips[key];
    if (!c) return null;
    const s = this.manifest.sprites[c.sprite];
    const buffer = s ? this.buffers.get(s.file) : undefined;
    if (!buffer) return null;
    // Guard against a fallback encoding whose length differs slightly.
    if (c.start >= buffer.duration) return null;
    return { buffer, start: c.start, dur: Math.min(c.dur, buffer.duration - c.start) };
  }
}

/** The shared sample bank. */
export const samples = new SampleBank();
